"use client"

import type { ComponentProps, ElementType } from "react"

import { SectionHeader, SeeAllButton } from "@/components/marketplace/layout/section-header"
import { CarouselDots, Grid, Heading, Reel, Row, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

interface CollectionBanner {
  id: string
  eyebrow?: string
  title: string
  cta?: string
  href?: string
  image: string
  serveId?: string
}

// Centered editorial banner: full-bleed photo under a flat wash, the eyebrow /
// headline / CTA stacked and centred. The CTA is a solid white pill — a strong
// tap affordance over the photo — rendered as a span, since the whole banner is
// already the link (a nested button would be interactive-in-interactive).
const Banner = ({ item, className, index = 0 }: { item: CollectionBanner; className: string; index?: number }) => {
  const Card: ElementType = item.href ? Link : "div"
  return (
    <Card
      {...(item.href ? { href: item.href } : {})}
      data-nelo-serve={item.serveId}
      style={{ animationDelay: `${index * 70}ms` }}
      className={`press bg-nelo-black-soft rounded-12x focus-visible:ring-palette-bar group motion-safe:animate-rise-in relative isolate overflow-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${className}`}
    >
      <TileImage
        src={item.image}
        alt=""
        fill
        sizes="(max-width: 768px) 280px, 25vw"
        className="duration-moderate ease-productive object-cover transition-transform group-hover:scale-105"
      />
      <span className="bg-nelo-black-soft/35 absolute inset-0 z-10" aria-hidden />
      <div className="text-fg-inverted absolute inset-0 z-20 flex flex-col items-center justify-center gap-300 p-500 text-center">
        {item.eyebrow && (
          <Text as="div" variant="eyebrow" tone="inherit" className="text-shadow-scrim">
            {item.eyebrow}
          </Text>
        )}
        <Heading as="div" variant="sm" family="display" tone="inherit" balance className="@3xl:text-heading-md text-shadow-scrim font-bold tracking-tight">
          {item.title}
        </Heading>
        {item.cta && (
          <Text
            as="span"
            variant="bodySm"
            family="display"
            tone="default"
            className="bg-surface ease-productive duration-quick mt-100 rounded-full px-400 py-200 font-semibold shadow-sm transition-transform group-hover:scale-105"
          >
            {item.cta}
          </Text>
        )}
      </div>
    </Card>
  )
}

interface CollectionBannersProps {
  title: string
  accent?: string
  items: CollectionBanner[]
  seeAllHref?: string
  headerSize?: ComponentProps<typeof SectionHeader>["size"]
  numeral?: string
}

export const CollectionBanners = ({ title, accent, items, seeAllHref, headerSize, numeral }: CollectionBannersProps) => {
  const { ref, index, stops, overflowing, goTo } = useReelPagination(items.length)
  // Even rows over orphans: the largest of 4/3/2 that divides the count, else cap at 4.
  const cols = (([4, 3, 2].find((candidate) => items.length % candidate === 0) ?? Math.min(items.length, 4)) || 2) as 2 | 3 | 4
  return (
    <section className="@container">
      <SectionHeader title={title} accent={accent} size={headerSize} numeral={numeral} action={<SeeAllButton href={seeAllHref} />} className="px-gut mb-500" />
      <Reel ref={ref} gap="300" snap inset className="@3xl:hidden">
        {items.map((item, position) => (
          <Banner key={item.id} item={item} index={position} className="h-45 w-70" />
        ))}
      </Reel>
      <div className="px-gut hidden @3xl:block">
        <Grid cols={cols} gap="400">
          {items.map((item, position) => (
            <Banner key={item.id} item={item} index={position} className="aspect-tile" />
          ))}
        </Grid>
      </div>
      {overflowing && (
        <Row justify="center" className="mt-300 @3xl:hidden">
          <CarouselDots tone="plain" count={stops} index={index} onChange={goTo} />
        </Row>
      )}
    </section>
  )
}
